/**
 * T065-T070 (001-catalogue-admin-screens, US3 §2/§3, FR-015 to FR-019, FR-018). The generated tier
 * price list: `PagedTable` with `?page=`/`?isStale=` in the URL (T065), origin/staleness badges
 * read straight from `source`/`isStale` (T066), filtered by the tier's own purchase-mode flags
 * (T067, D-02), a Generate action that reports `rowsGenerated` and lists every skip (T068), a
 * manual rate edit that comes back `manually_set` from the server (T069), and a rule-attribution
 * panel that states plainly the deciding rule is not yet reported (T070, GAP-3) — never derived
 * here (constitution I).
 *
 * **No separate `/tiers/:tierId/prices/:priceId/edit` route.** `TiersController.cs` has no
 * `GET /tiers/{id}/prices/{priceId}` — only the paged list and the two writes — so there is no
 * single row to hydrate a bookmarked/refreshed edit URL from. This screen expands the row it
 * already fetched, in place, via `PagedTable`'s `subRow` instead of inventing a fetch the API
 * doesn't offer (constitution I: no fabricated workaround for a real API gap).
 *
 * T091: "Bulk upload" links to `/imports?kind=tier_price` — the querystring entry point
 * `pages/imports/List.tsx`'s own header comment names for this bucket to use, landing in the same
 * validate → diff → commit wizard `POST /imports?kind=tier_price` drives (FR-018).
 */
import { useState } from "react";
import { Link, useLoaderData, useNavigate, useOutletContext, useRevalidator, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import toast from "react-hot-toast";
import { PiInfoDuotone as InfoIcon, PiPlayDuotone as Play, PiUploadSimpleDuotone as UploadIcon } from "react-icons/pi";
import { Alert, Button, Text, Title } from "rizzui";
import { EmptyState, ErrorSurface, Field, useModal } from "@steelyard/theme";
import { SelectField } from "../../../components/select-field";
import { PagedTable } from "../../../components/table/data-table";
import { ListHeader } from "../../../components/table/list-header";
import { SearchInput } from "../../../components/table/search-input";
import { RuleAttribution } from "../../../components/rule-attribution";
import { PricingFlow } from "../../../components/pricing-flow";
import { useConfirm } from "../../../components/confirm-modal";
import { ROUNDING_NAME } from "../../../lib/tier-text";
import { formatMoney } from "../../../lib/format";
import { listCustomers } from "../../../lib/services/customers";
import { tierPriceColumns } from "./columns";
import { can } from "../../../lib/permissions";
import { hrefWith, readPaging } from "../../../lib/paging";
import { tierPath } from "../../../lib/paths";
import type { AccountSummary, PermissionState } from "../../../types";
import {
  generateTierPrices,
  byKey,
  getTier,
  getTierBySlug,
  getTierPrices,
  listUnits,
  revertTierPriceToRule,
  updateTierPrice,
  type GenerationSkip,
  type TierPriceRow,
  type GstTreatment,
} from "../../../lib/services/catalog";

export async function tierPricesLoader({ params, request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const { page, pageSize } = readPaging(url.searchParams);
  const isStaleParam = url.searchParams.get("isStale");
  const isStale = isStaleParam === "1" ? true : isStaleParam === "0" ? false : undefined;
  // Server search over every row of the tier, not just this page — product, SKU or unit.
  const q = url.searchParams.get("q") ?? undefined;

  // Three independent reads, one round trip each, in parallel — the tier (for its name and its
  // FR-017 purchase-mode flags), this page of generated prices, and the unit reference table
  // (T067 needs a unit's `type` to tell a "piece" row from a "unit" row; see `isPieceUnit` below).
  // Plus the All / Stale only tab counts: the list's own `totalCount` for the tab being shown, and a
  // one-row read for the other — the API's numbers, never counted here.
  // The tier first: the URL carries its slug, and every other read takes its id.
  const tier = await byKey(request, params.slug!, getTier, getTierBySlug, tierPath);
  const tierId = tier.id;
  const [prices, units, allTotal, staleTotal] = await Promise.all([
    getTierPrices(tierId, { page, isStale, pageSize, q }),
    listUnits(),
    isStale === undefined ? null : getTierPrices(tierId, { page: 1, pageSize: 1, q }).catch(() => null),
    isStale === true ? null : getTierPrices(tierId, { page: 1, pageSize: 1, isStale: true, q }).catch(() => null),
  ]);
  if (!prices) throw new Error("The API returned no body for this tier's price list.");
  const allCount = isStale === undefined ? Number(prices.totalCount) : allTotal ? Number(allTotal.totalCount) : null;
  const staleCount = isStale === true ? Number(prices.totalCount) : staleTotal ? Number(staleTotal.totalCount) : null;
  return { tierId, tier, prices, units, page, isStale, q, allCount, staleCount };
}

/**
 * Shown once Generate prices succeeds — step 4 of the pricing flow, which happens on another screen
 * (a customer's Assign terms), so the admin is told where to go rather than left on this list.
 * `onTier` is the API's own count of active customers already on the tier, or null when unread.
 */
function GeneratedNextStep({
  tierId,
  tierName,
  rowsGenerated,
  skipped,
  onTier,
  onClose,
}: {
  tierId: string;
  tierName: string;
  rowsGenerated: number;
  skipped: number;
  onTier: number | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const go = (to: string) => {
    onClose();
    navigate(to);
  };
  return (
    <div className="flex flex-col gap-4 p-6">
      <Title as="h4">
        {rowsGenerated} price{rowsGenerated === 1 ? "" : "s"} generated for {tierName}
      </Title>
      <Text>
        This tier&apos;s price list is ready. <strong>One step left:</strong> a customer pays these prices only once they are on{" "}
        {tierName}.
      </Text>
      {onTier !== null ? (
        <Alert color={onTier > 0 ? "success" : "warning"} variant="flat">
          {onTier > 0
            ? `${onTier} active customer${onTier === 1 ? " is" : "s are"} on this tier already — they pay these prices from their next order.`
            : "No customer is on this tier yet, so nobody pays these prices until you put one on it."}
        </Alert>
      ) : null}
      <div>
        <Text className="mb-1 font-semibold text-gray-900">To put a customer on {tierName}</Text>
        <ol className="list-decimal space-y-1 ps-5 text-sm text-gray-700">
          <li>Open Customers and choose the customer.</li>
          <li>
            Press <strong>Assign terms</strong> (or <strong>Change terms</strong> if they already have a tier).
          </li>
          <li>Pick {tierName}, set a credit limit and payment term, and save. They become a B2B customer on these prices.</li>
        </ol>
      </div>
      {skipped > 0 ? (
        <Text className="text-sm text-orange-dark">
          {skipped} price{skipped === 1 ? " was" : "s were"} skipped — they are listed on this page.
        </Text>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Stay here
        </Button>
        {onTier ? (
          <Button type="button" variant="outline" onClick={() => go(`/customers?tierId=${tierId}`)}>
            Customers on this tier
          </Button>
        ) : null}
        <Button type="button" onClick={() => go("/customers")}>
          Choose a customer
        </Button>
      </div>
    </div>
  );
}

export default function TierPrices() {
  const { tierId, tier, prices, units, isStale, q, allCount, staleCount } = useLoaderData() as Awaited<ReturnType<typeof tierPricesLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const revalidator = useRevalidator();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { openModal, closeModal } = useModal();

  const [error, setError] = useState<unknown>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rateDraft, setRateDraft] = useState("");
  const [gstDraft, setGstDraft] = useState<GstTreatment>("exclusive");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [lastGenerate, setLastGenerate] = useState<{ rowsGenerated: number; skipped: GenerationSkip[] } | null>(null);

  if (!can(permissionState, "pricing.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view this tier's price list" />;
  }
  // pricing.write, not tiers.write: this screen generates and edits price ROWS, the same
  // permission `catalogue/skus/BasePrices.tsx`'s `canWrite` already gates for the base-price
  // editor — tier settings themselves (T061, `tiers.write`) are a different screen entirely.
  const canWrite = can(permissionState, "pricing.administer");

  // T067 (FR-017, D-02): "piece" is the Piece/count unit — a permission the tier states, mirrored
  // from `PricingQueryHandler.IsPurchasable`'s own comment on the backend ("the Piece unit is the
  // 'piece' of D-02's piece/unit split ... the two tier flags map onto the unit's identity rather
  // than onto anything stored per row"). D-02's flags decide what CUSTOMERS may buy, not what an
  // admin sees: every row stays listed (hiding them broke paging against the server's totalCount
  // and hid the Default Tier's unit rates), and the page only counts the ones customers can't buy.
  const unitTypeById = new Map(units.items.map((u) => [u.id, u.type]));
  const unitCodeById = new Map(units.items.map((u) => [u.id, u.code]));
  const isPieceUnit = (unitId: string) => unitTypeById.get(unitId) === "count";
  const permitted = (row: TierPriceRow) => (isPieceUnit(row.unitId) ? tier.allowPiecePurchase : tier.allowUnitPurchase);
  const notBuyableOnThisPage = prices.items.filter((row) => !permitted(row)).length;
  // A staleness switch resets the page but keeps `?pageSize=`.
  const staleHref = (stale: boolean) => hrefWith(`${tierPath(tier.slug)}/prices`, searchParams, { isStale: stale ? "1" : undefined, page: undefined });
  const searchHref = (next: string | undefined) => hrefWith(`${tierPath(tier.slug)}/prices`, searchParams, { q: next, page: undefined });

  // Generating recalculates every rule-made row, so it is asked for, not one click away.
  function askToGenerate() {
    confirm({
      title: `Generate prices for ${tier.name}`,
      consequence:
        "Every price that came from a rule is recalculated from today's base prices and this tier's rules. Prices set by hand are kept as they are.",
      confirmLabel: "Generate prices",
      onConfirm: () => void handleGenerate(),
    });
  }

  async function handleGenerate() {
    setError(null);
    setGenerating(true);
    try {
      const result = await generateTierPrices(tierId);
      // A skip names its SKU and unit by the codes Catalog sends with it — never by id.
      setLastGenerate({ rowsGenerated: result.rowsGenerated, skipped: result.skipped });
      revalidator.revalidate();
      // Active customers already on the tier, by the API's own count; unread rather than guessed.
      const onTier = can(permissionState, "customers.read")
        ? await listCustomers({ page: 1, pageSize: 1, tierId, status: "1" }).then((r) => r.totalCount).catch(() => null)
        : null;
      openModal({
        size: "lg",
        view: (
          <GeneratedNextStep
            tierId={tierId}
            tierName={tier.name}
            rowsGenerated={result.rowsGenerated}
            skipped={result.skipped.length}
            onTier={onTier}
            onClose={closeModal}
          />
        ),
      });
    } catch (err) {
      setError(err);
      toast.error("Could not generate prices");
    } finally {
      setGenerating(false);
    }
  }

  function toggleRow(row: TierPriceRow) {
    setExpandedId((current) => (current === row.id ? null : row.id));
    setRateDraft(String(row.rate));
    setGstDraft(row.gstTreatment);
  }

  async function saveRate(row: TierPriceRow) {
    const rate = Number(rateDraft);
    if (!Number.isFinite(rate) || rate <= 0) {
      toast.error("Enter a rate greater than 0");
      return;
    }
    setSavingId(row.id);
    setError(null);
    try {
      await updateTierPrice(tierId, row.id, rate, gstDraft);
      toast.success("Rate saved");
      setExpandedId(null);
      // T069: re-fetch rather than trust what was typed — the row must come back `manually_set`
      // from the server (FR-016), the same discipline `BasePrices.tsx`'s `setBasePrices` follows.
      revalidator.revalidate();
    } catch (err) {
      setError(err);
      toast.error("Could not save this rate");
    } finally {
      setSavingId(null);
    }
  }

  // ASPL-10159 (FR-045a). The confirm shows the API's dry-run figure — nothing is worked out here.
  async function askToRevert(row: TierPriceRow) {
    setError(null);
    setSavingId(row.id);
    try {
      const preview = await revertTierPriceToRule(tierId, row.id, true);
      confirm({
        title: `Revert ${row.skuCode} ${row.unitCode} to rule`,
        consequence: (
          <>
            The rate set by hand, {formatMoney(row.rate)}, is replaced by <strong>{formatMoney(preview.rate)}</strong>: the winning
            rule&apos;s {preview.discountPct}% off the base rate {formatMoney(preview.derivedFromBaseRate)}, with this tier&apos;s
            rounding, GST {preview.gstTreatment}. From then on, Generate prices recalculates this row.
          </>
        ),
        confirmLabel: "Revert to rule",
        onConfirm: () => void handleRevert(row),
      });
    } catch (err) {
      setError(err);
      toast.error("Could not revert this rate to its rule");
    } finally {
      setSavingId(null);
    }
  }

  async function handleRevert(row: TierPriceRow) {
    setSavingId(row.id);
    setError(null);
    try {
      await revertTierPriceToRule(tierId, row.id, false);
      toast.success("Rate reverted to rule");
      setExpandedId(null);
      revalidator.revalidate();
    } catch (err) {
      setError(err);
      toast.error("Could not revert this rate to its rule");
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title={`${tier.name} — Generated Prices`}
        count={prices.totalCount}
        breadcrumb={[
          { name: "Home", href: "/" },
          { name: "Pricing · Tiers", href: "/tiers" },
          { name: tier.name, href: tierPath(tier.slug) },
          { name: "Prices" },
        ]}
        description={
          tier.isDefault
            ? "The Default Tier's prices — the base prices set on each SKU."
            : "What customers on this tier pay, per SKU and unit."
        }
      />

      {tier.isDefault ? null : <PricingFlow current="generate" tierSlug={tier.slug} />}

      {/* What Generate does and does not touch, in the handler's own terms (GenerateTierPricesHandler,
          pricing-admin.md) — 2026-09-29, the owner found this screen hard to follow. */}
      <Alert color="info" variant="flat" icon={<InfoIcon aria-hidden className="size-5" />}>
        {tier.isDefault ? (
          <Text className="text-sm">
            The Default Tier is never generated — its rows <strong>are</strong> the base prices, edited on each SKU&apos;s Base prices
            screen. Every other tier is generated from them.
          </Text>
        ) : (
          <ul className="list-disc space-y-1 ps-4 text-sm">
            <li>
              <strong>Generate prices</strong> recalculates this list: for every SKU and unit that has a base price, it takes the
              winning rule&apos;s discount off the base rate, rounds to paise, then applies this tier&apos;s rounding (
              {ROUNDING_NAME[tier.priceRoundingMode]}).
            </li>
            <li>
              <strong>From a rule</strong> rows are recalculated every time. <strong>Set by hand</strong> rows — a rate changed here by clicking the
              row — are kept, and no rule reaches them. <strong>Revert to rule</strong> on the row puts one back under the rules.
            </li>
            <li>
              <strong>Stale</strong> means the base price or a rule changed since the row was calculated. Generate again, or re-apply
              from Stale prices, to bring it up to date.
            </li>
            <li>Inactive SKUs are not priced. A discount that would take a price to zero or below is skipped and listed.</li>
          </ul>
        )}
      </Alert>

      {error ? <ErrorSurface error={error} action="load or change this tier's prices" /> : null}

      {lastGenerate ? (
        <Alert color={lastGenerate.skipped.length > 0 ? "warning" : "success"} variant="flat">
          <Text className="text-sm font-medium">
            {lastGenerate.rowsGenerated} row{lastGenerate.rowsGenerated === 1 ? "" : "s"} generated.
          </Text>
          {lastGenerate.skipped.length > 0 ? (
            <div className="mt-2">
              <Text className="text-sm font-medium">
                {lastGenerate.skipped.length} skipped — listed, never hidden (T068):
              </Text>
              <ul className="list-disc ps-5 font-mono text-xs">
                {lastGenerate.skipped.map((skip, i) => (
                  <li key={`${skip.skuId}-${skip.unitId}-${i}`}>
                    SKU {skip.skuCode ? `${skip.skuCode}${skip.skuName ? ` — ${skip.skuName}` : ""}` : "—"} · unit {skip.unitCode ?? unitCodeById.get(skip.unitId) ?? "—"} · {skip.discountPct}% discount
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Alert>
      ) : null}

      {notBuyableOnThisPage > 0 ? (
        <Text className="text-xs text-gray-500">
          {notBuyableOnThisPage} row{notBuyableOnThisPage === 1 ? "" : "s"} on this page {notBuyableOnThisPage === 1 ? "is" : "are"} not
          offered to {tier.name} customers — the tier allows{" "}
          {tier.allowPiecePurchase && !tier.allowUnitPurchase ? "piece purchases only" : !tier.allowPiecePurchase && tier.allowUnitPurchase ? "unit purchases only" : "neither piece nor unit purchases"} (D-02).
        </Text>
      ) : null}

      <PagedTable<TierPriceRow>
        actions={
          <>
            {/* The importer commits under catalogue.administer (Catalog's ImportController), which a
                Sales/Account Manager may not hold even with pricing.administer. */}
            {canWrite && can(permissionState, "catalogue.administer") ? (
              <Link to="/imports?kind=tier_price">
                <Button as="span" variant="outline">
                  <UploadIcon aria-hidden className="me-1.5 size-4" />
                  Bulk upload
                </Button>
              </Link>
            ) : null}
            {/* 422 on the Default Tier (pricing-admin.md): its rows are the base prices, so no Generate at all. */}
            {canWrite && !tier.isDefault ? (
              <Button onClick={askToGenerate} disabled={generating}>
                <Play aria-hidden className="me-1.5 size-4" />
                {generating ? "Generating…" : "Generate prices"}
              </Button>
            ) : null}
          </>
        }
        result={prices}
        caption={`Generated prices for ${tier.name}`}
        columns={tierPriceColumns({ canWrite, expandedId, onToggle: toggleRow, onRevert: (row) => void askToRevert(row) })}
        getRowId={(row) => row.id}
        status={{
          label: "Show",
          options: [
            { label: "All prices", href: staleHref(false), active: !isStale, count: allCount },
            { label: "Stale only", href: staleHref(true), active: Boolean(isStale), count: staleCount },
          ],
        }}
        search={<SearchInput value={q} placeholder="Search by product, SKU or unit…" onSearch={(next) => navigate(searchHref(next))} />}
        clear={{
          active: Boolean(isStale || q),
          href: hrefWith(`${tierPath(tier.slug)}/prices`, searchParams, { isStale: undefined, q: undefined, page: undefined }),
        }}
        subRow={(row) =>
          row.id === expandedId ? (
            <div className="flex flex-col gap-3 py-2">
              <RuleAttribution tierSlug={tier.slug} row={row} />
              {canWrite ? (
                <div className="flex flex-wrap items-end gap-3">
                  <Field
                    label="New rate"
                    name={`rate-${row.id}`}
                    inputMode="decimal"
                    value={rateDraft}
                    onChange={(event) => setRateDraft(event.target.value)}
                    className="w-40 font-mono"
                  />
                  {/* D-04: GST treatment is configurable per price. */}
                  <SelectField
                    label="GST"
                    name={`gst-${row.id}`}
                    value={gstDraft}
                    onChange={(event) => setGstDraft(event.target.value as GstTreatment)}
                  >
                    <option value="inclusive">Inclusive</option>
                    <option value="exclusive">Exclusive</option>
                  </SelectField>
                  <Button size="sm" disabled={savingId === row.id} onClick={() => saveRate(row)}>
                    {savingId === row.id ? "Saving…" : "Save rate"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setExpandedId(null)}>
                    Cancel
                  </Button>
                  {row.source === "manually_set" ? (
                    <Button size="sm" variant="outline" disabled={savingId === row.id} onClick={() => void askToRevert(row)}>
                      Revert to rule
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null
        }
        empty={
          <EmptyState title={q ? "No prices matched" : isStale ? "No stale prices" : "No prices generated yet"}>
            {q
              ? "No product, SKU or unit on this tier matches that search. Clear it to see every price."
              : isStale
                ? "Nothing on this tier is currently stale."
                : tier.isDefault
                  ? "No base prices are set yet — set them on each SKU's Base prices screen."
                  : "Set this tier's discount rules, then press Generate prices to build its price list."}
          </EmptyState>
        }
      />
    </div>
  );
}
